import { createReducer, on } from '@ngrx/store';

import * as actions from '../actions';
import {
  DataState,
  ProductPrepackEntities,
  ProductSizeEntities,
  ProductType,
  ProductVariantEntities,
} from '../models/config-strip-state.models';
import { pricesReducer } from '../prices/prices.reducer';

export const dataInitialState: DataState = {
  product: null,
  variants: {},
  sizes: {},
  prepacks: {},
  prices: {
    now: {},
    future: {},
  },
  scaleSets: null,
  deliveryShops: null,
  validators: [],
  sizeRuns: null,
};

export const dataConfigStripReducer = createReducer<DataState>(
  dataInitialState,
  on(
    actions.loadProductSuccess,
    actions.cachedProductResolved,
    (state: DataState, { product }) => ({
      ...state,
      product,
      variants: productVariantReducer(product),
      sizes: productSizeReducer(product),
      prepacks: productPrepackReducer(product),
      scaleSets: product.scaleSets ?? null,
    }),
  ),
  on(actions.setDeliveryShops, (state, { deliveryShops }) => ({
    ...state,
    deliveryShops,
  })),
  on(actions.clearState, (state) => ({
    ...dataInitialState,
    product: state.product,
  })),
  on(
    actions.loadPricesSuccess,
    actions.updatePricesWithDiscounts,
    (state, { prices }) => {
      return {
        ...state,
        prices: pricesReducer(prices),
      };
    },
  ),
  on(actions.setValidators, (state, { validators }) => ({
    ...state,
    validators,
  })),
  on(actions.loadSizeRunsSuccess, (state, { sizeRuns }) => {
    return {
      ...state,
      sizeRuns,
    };
  }),
);

function productVariantReducer(product: ProductType): ProductVariantEntities {
  if (!product.children) {
    return {};
  }

  return product.children.reduce((acc, child) => {
    if (!child.productId) {
      return acc;
    }

    return {
      ...acc,
      [child.productId]: {
        ...child,
        sizeReferences: child.children?.map((size) => size.productId) ?? [],
      },
    };
  }, {} as ProductVariantEntities);
}

function productSizeReducer(product: ProductType): ProductSizeEntities {
  if (!product.children) {
    return {};
  }

  let sizeEntities: ProductSizeEntities = {};

  product.children.forEach((variant) => {
    if (!variant.children) {
      return;
    }

    variant.children.forEach((size) => {
      sizeEntities = {
        ...sizeEntities,
        [size.productId]: {
          ...size,
          variantReference: variant.productId,
          // keep only consumable inventories in inventoryItems as there are too much place where it is consumed from size entity
          inventoryItems:
            size.inventoryItems?.filter((i) => !i.isNotConsumable) ?? [],
          isNotConsumableInventories:
            size.inventoryItems?.filter((i) => i.isNotConsumable) ?? [],
        },
      };
    });
  });

  return sizeEntities;
}

function productPrepackReducer(product: ProductType): ProductPrepackEntities {
  if (!product.prepacks) {
    return {};
  }

  return product.prepacks?.reduce(
    (aggr, prepack) => ({
      ...aggr,
      [prepack.productId]: prepack,
    }),
    {},
  );
}
